import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from '../ui/date-picker';
import TimeSlotCards from '../dashboard/time-slot-cards';
import { Calendar, Clock } from 'lucide-react';
import type { BookableTimeSlot } from '../../model/professional/availability-model';
import { formatDateInTimeZone } from '../../utils/timezone';

interface TimeSlotSelectorProps {
  availableSlots: BookableTimeSlot[];
  selectedDate: Date | null;
  selectedSlot: BookableTimeSlot | null;
  onSelectDate: (date: Date) => void;
  onSelectSlot: (slot: BookableTimeSlot) => void;
  availableDates?: string[]; 
}

const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
  availableSlots,
  selectedDate,
  selectedSlot,
  onSelectDate,
  onSelectSlot,
  availableDates = []
}) => {
  const { t } = useTranslation();
  
  const slotsForSelectedDate = availableSlots;

  // Référence pour la section des créneaux horaires
  const timeSlotsRef = useRef<HTMLDivElement>(null);
  
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(e.target.value);
    onSelectDate(newDate);
  };
  
  useEffect(() => {
    if (!selectedDate && availableDates && availableDates.length > 0) {
      // Sélectionner automatiquement le premier jour disponible
      const firstAvailableDate = new Date(availableDates[0]);
      onSelectDate(firstAvailableDate);
    }
  }, [availableDates]);

  
  // Convertir les créneaux en options pour l'affichage
  const timeSlotOptions = slotsForSelectedDate.map(slot => ({
    value: slot.timeSlotId,
    label: `${slot.startTime} - ${slot.endTime}`
  }));
  
  const handleTimeSlotSelect = (timeSlotId: string) => {
    const slot = availableSlots.find(s => s.timeSlotId === timeSlotId);
    if (slot) onSelectSlot(slot);
  };
  
  const today = formatDateInTimeZone(new Date(), 'yyyy-MM-dd');

  const formattedDate = selectedDate ? formatDateInTimeZone(selectedDate, 'yyyy-MM-dd') : today;
  
  return (
    <div className="space-y-6">
      <div className="flex items-center mb-6">
        <div className="mr-3 p-2 bg-primary/10 rounded-full">
          <Calendar className="h-5 w-5 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800">
          {t('booking.selectDateTime', 'Choisissez une date et un horaire')}
        </h3>
      </div>
      
      {/* Sélecteur de date */}
      <DatePicker
        name="appointmentDate"
        value={formattedDate}
        onChange={handleDateChange}
        min={today}
        label=""
        forceLight={true}
        required
        className="w-full"
        availableDates={availableDates}
      />
      
      {/* Créneaux horaires */}
      {selectedDate && (
        <div ref={timeSlotsRef} className="mt-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="mb-4 flex items-center">
            <Clock className="h-4 w-4 text-primary mr-2" />
            <span className="text-sm font-medium text-gray-700">
              {t('booking.selectTime', 'Sélectionnez un horaire')}
            </span>
          </div>
          {(() => {
            return slotsForSelectedDate.length > 0 ? (
              <TimeSlotCards
                options={timeSlotOptions}
                selectedValue={selectedSlot?.timeSlotId || ''}
                onSelect={handleTimeSlotSelect}
                forceLight={true}
                isLoading={false}
              />
            ) : (
              <div className="py-8 text-center">
                <p className="text-gray-500 text-sm">
                  {t('booking.noTimeSlots', 'Aucun créneau disponible pour cette date')}
                </p>
                <p className="text-primary text-xs mt-2">
                  {t('booking.tryAnotherDate', 'Veuillez sélectionner une autre date')}
                </p>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default TimeSlotSelector;
